import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (email, password, name) => api.post('/auth/register', { email, password, name });
export const getMe = () => api.get('/auth/me');
export const updateTheme = (theme, accentColor, fontTheme) => api.put('/auth/theme', { theme, accent_color: accentColor, font_theme: fontTheme });

// Leads
export const getLeads = () => api.get('/leads');
export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const importLeads = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/leads/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Followups
export const getFollowups = (leadId) => api.get(`/leads/${leadId}/followups`);
export const createFollowup = (leadId, type, notes) => api.post('/followups', { lead_id: leadId, type, notes });
export const getFollowupCount = (leadId) => api.get(`/followups/count/${leadId}`);

// Reminders
export const createReminder = (leadId, scheduledAt, message, notifyEmail = true, notifySms = false) => 
  api.post('/reminders', { lead_id: leadId, scheduled_at: scheduledAt, message, notify_email: notifyEmail, notify_sms: notifySms });
export const getReminders = (leadId) => api.get(`/reminders/${leadId}`);
export const deleteReminder = (id) => api.delete(`/reminders/${id}`);
export const testEmailNotification = () => api.post('/reminders/test-email');
export const testSmsNotification = () => api.post('/reminders/test-sms');

// Notification Settings
export const getNotificationSettings = () => api.get('/auth/notifications');
export const updateNotificationSettings = (settings) => api.put('/auth/notifications', settings);

// Admin / Team Management
export const getTeamMembers = () => api.get('/admin/team');
export const createTeamMember = (email, password, name, role = 'user') => api.post('/admin/team', { email, password, name, role });
export const deleteTeamMember = (userId) => api.delete(`/admin/team/${userId}`);
export const updateUserRole = (userId, role) => api.put(`/admin/team/${userId}/role`, { role });

// Bulk Actions
export const bulkDeleteLeads = (leadIds) => api.post('/leads/bulk-delete', leadIds);
export const bulkUpdateStatus = (leadIds, status) => api.post(`/leads/bulk-status?status=${status}`, leadIds);
export const deleteAllLeads = () => api.delete('/leads/delete-all');

// Export
export const exportLeads = () => window.open(`${BACKEND_URL}/api/leads/export`, '_blank');

// Stats
export const getStats = () => api.get('/stats');

// Super Admin - Organizations
export const getOrganizations = () => api.get('/superadmin/organizations');
export const createOrganization = (name, slug, plan) => api.post('/superadmin/organizations', { name, slug, plan });
export const updateOrganization = (orgId, data) => api.put(`/superadmin/organizations/${orgId}`, data);
export const deleteOrganization = (orgId) => api.delete(`/superadmin/organizations/${orgId}`);
export const createOrgOwner = (orgId, name, email, password) => api.post(`/superadmin/organizations/${orgId}/owner`, { name, email, password });
export const getSuperadminStats = () => api.get('/superadmin/stats');

// Calendar Events
export const getCalendarEvents = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/calendar/events?${params.toString()}`);
};
export const createCalendarEvent = (data) => api.post('/calendar/events', data);
export const updateCalendarEvent = (eventId, data) => api.put(`/calendar/events/${eventId}`, data);
export const deleteCalendarEvent = (eventId) => api.delete(`/calendar/events/${eventId}`);
export const getUpcomingEvents = (days = 7) => api.get(`/calendar/upcoming?days=${days}`);

// Tasks
export const getTasks = (status) => {
  const params = status ? `?status=${status}` : '';
  return api.get(`/tasks${params}`);
};
export const getMyTasks = () => api.get('/tasks/my');
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (taskId, data) => api.put(`/tasks/${taskId}`, data);
export const completeTask = (taskId) => api.post(`/tasks/${taskId}/complete`);
export const deleteTask = (taskId) => api.delete(`/tasks/${taskId}`);

// Payment Reminders
export const getPaymentReminderTemplates = () => api.get('/payment-reminders/templates');
export const createPaymentReminderTemplate = (data) => api.post('/payment-reminders/templates', data);
export const deletePaymentReminderTemplate = (templateId) => api.delete(`/payment-reminders/templates/${templateId}`);
export const getScheduledReminders = (status) => {
  const params = status ? `?status=${status}` : '';
  return api.get(`/payment-reminders/scheduled${params}`);
};
export const schedulePaymentReminder = (data) => api.post('/payment-reminders/scheduled', data);
export const cancelScheduledReminder = (reminderId) => api.delete(`/payment-reminders/scheduled/${reminderId}`);
export const sendPaymentReminderNow = (data) => api.post('/payment-reminders/send-now', data);
export const previewPaymentReminder = (data) => api.post('/payment-reminders/preview', data);

// Organization Features
export const getOrgFeatures = () => api.get('/org/features');

// AI Assistant
export const sendAIMessage = (message, sessionId) => api.post('/ai/chat', { message, session_id: sessionId });
export const getAIHistory = (sessionId) => api.get(`/ai/history${sessionId ? `?session_id=${sessionId}` : ''}`);
export const clearAIHistory = () => api.delete('/ai/history');

export default api;
